/**
 * SummaryTab — renders the AI-generated meeting summary.
 *
 * When summary is null: shows the EmptyState placeholder.
 * When summary is present: shows an AI-register Card with:
 *   - sparkle icon + label "AI summary — based on the transcript"
 *   - overview paragraph
 *   - optional sections: Key moments / Action items / Decisions
 *     (each only rendered when non-empty)
 *   - each item has a timestamp link (clock icon + mm:ss or wall-clock)
 *     that switches to the Transcript tab + seeks the player.
 *
 * Hebrew RTL: handled via dir="auto" per text block so Hebrew text
 * right-aligns naturally without extra logic.
 */

import { EmptyState } from "../../ui/EmptyState";
import { Icon } from "../../ui/icons";
import { t } from "../../i18n";
import { formatWallClock } from "../../lib/timestamps";
import type { TranscriptSummary, SummaryItem } from "../MeetingReview";

interface Props {
  /** Null = summary not yet generated → show empty state. */
  summary: TranscriptSummary | null;
  /** Meeting recorded_at ISO string (for wall-clock timestamps). May be null. */
  recordedAt: string | null;
  /**
   * Called when the user clicks a timestamp link.
   * The parent switches to the Transcript tab and seeks the player to ms.
   */
  onSeekAndSwitch: (ms: number) => void;
  /** Regenerate offer (M2). Hidden when locked / not ready. */
  canRegenerate?: boolean;
  regenerating?: boolean;
  onRegenerate?: () => void;
}

function TimestampLink({
  ms,
  recordedAt,
  onSeekAndSwitch,
}: {
  ms: number;
  recordedAt: string | null;
  onSeekAndSwitch: (ms: number) => void;
}) {
  const label = formatWallClock(recordedAt, ms);
  return (
    <button
      type="button"
      onClick={() => onSeekAndSwitch(ms)}
      title="Jump to this moment in the transcript"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        background: "none",
        border: "none",
        padding: "0 4px",
        cursor: "pointer",
        color: "var(--accent)",
        fontSize: 12,
        fontFamily: "var(--font-mono, monospace)",
        verticalAlign: "middle",
      }}
    >
      <Icon name="clock" size={12} />
      {label}
    </button>
  );
}

function SummarySection({
  heading,
  items,
  recordedAt,
  onSeekAndSwitch,
  showOwner = false,
}: {
  heading: string;
  items: SummaryItem[];
  recordedAt: string | null;
  onSeekAndSwitch: (ms: number) => void;
  showOwner?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <h4
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          margin: "0 0 10px",
        }}
      >
        {heading}
      </h4>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {items.map((item, i) => (
          <li
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginBottom: 8,
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            {/* Bullet dot */}
            <span
              style={{
                flexShrink: 0,
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--accent)",
                marginTop: 8,
              }}
            />
            <span style={{ flex: 1 }}>
              <span dir="auto">{item.text}</span>
              {showOwner && item.owner && (
                <span
                  className="muted"
                  style={{ fontSize: 12, marginLeft: 6 }}
                >
                  — {item.owner}
                </span>
              )}
              <TimestampLink
                ms={item.start_ms}
                recordedAt={recordedAt}
                onSeekAndSwitch={onSeekAndSwitch}
              />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SummaryTab({ summary, recordedAt, onSeekAndSwitch, canRegenerate, regenerating, onRegenerate }: Props) {
  if (!summary) {
    return (
      <div
        role="tabpanel"
        aria-labelledby="tab-summary"
        data-testid="summary-tab"
      >
        <EmptyState
          icon="sparkle"
          title={t("summary.emptyTitle")}
          description={t("summary.emptyBody")}
        />
        {canRegenerate && onRegenerate && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button type="button" className="secondary" disabled={regenerating} onClick={onRegenerate}>
              {regenerating ? t("summary.regenerating") : t("summary.generate")}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      role="tabpanel"
      aria-labelledby="tab-summary"
      data-testid="summary-tab"
    >
      {/* AI-register card */}
      <div
        className="card summary-ai-card"
        style={{
          marginTop: 16,
          borderColor: "color-mix(in srgb, var(--accent) 25%, transparent)",
        }}
      >
        {/* Header: sparkle icon + label */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <span style={{ color: "var(--accent)", flexShrink: 0 }}>
            <Icon name="sparkle" size={16} />
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {t("summary.aiLabel")}
          </span>
          {canRegenerate && onRegenerate && (
            <button type="button" className="secondary"
              style={{ marginInlineStart: "auto", fontSize: 12, padding: "3px 10px" }}
              disabled={regenerating} onClick={onRegenerate}>
              {regenerating ? t("summary.regenerating") : t("summary.regenerate")}
            </button>
          )}
        </div>

        {/* Overview */}
        <p
          dir="auto"
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.65,
            color: "var(--text)",
          }}
        >
          {summary.overview}
        </p>

        {/* Key moments */}
        <SummarySection
          heading={t("summary.keyMoments")}
          items={summary.key_points}
          recordedAt={recordedAt}
          onSeekAndSwitch={onSeekAndSwitch}
        />

        {/* Action items */}
        <SummarySection
          heading={t("summary.actionItems")}
          items={summary.action_items}
          recordedAt={recordedAt}
          onSeekAndSwitch={onSeekAndSwitch}
          showOwner
        />

        {/* Decisions */}
        <SummarySection
          heading={t("summary.decisions")}
          items={summary.decisions}
          recordedAt={recordedAt}
          onSeekAndSwitch={onSeekAndSwitch}
        />
      </div>
    </div>
  );
}
